/**
 * API Client for Cloud Functions via API Gateway
 *
 * This client routes all function calls through the API Gateway to ensure
 * proper CORS handling and centralized request management.
 */

import { emulatorPorts } from '../firebase/emulatorPorts';

// `firebase/auth` is imported lazily inside callFunction (at request time) rather than
// at module top: importing it eagerly pulls in `undici`, which the jsdom test transform
// can't parse — so any component transitively importing a service would fail to render
// in tests. Browser bundling is unaffected (dynamic import resolves to the same module).

// A newer deployed release than the one this tab is running → dispatch a one-shot event so the app can offer a
// "Refresh to update" prompt. The gateway stamps X-App-Version on every response; we compare to our build tag.
// A plain reload picks up the new bundle (index.html is served no-cache; no service worker to unregister). We
// never try to clear the HTTP cache from JS — hashed assets are immutable and index.html always revalidates.
let updateNotified = false;
function checkAppVersionDrift(serverVersion: string | null): void {
  try {
    const client = process.env.REACT_APP_VERSION || 'dev';
    if (updateNotified || !serverVersion || serverVersion === 'dev' || client === 'dev') return;
    if (serverVersion !== client) {
      updateNotified = true;
      window.dispatchEvent(new CustomEvent('wt:update-available', { detail: { from: client, to: serverVersion } }));
    }
  } catch { /* never break a request on version housekeeping */ }
}

/** Base URL for a named Cloud Function (defaults to the apiGateway). Mirrors the env
 *  auto-detection so sibling onRequest functions (e.g. conversationMedia) resolve too. */
export const getFunctionUrl = (name = 'apiGateway'): string => {
  const region = 'us-central1';

  // Auto-detect environment from hostname (prod vs staging → the matching project's Cloud Functions).
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isStaging = hostname.includes('staging.wisetree.ai')
    || hostname.includes('wisetree-ai-planner-staging.web.app')
    || hostname.includes('wisetree-ai-planner-staging.firebaseapp.com');

  let functionsProjectId: string;
  if (process.env.REACT_APP_FUNCTIONS_PROJECT_ID) {
    functionsProjectId = process.env.REACT_APP_FUNCTIONS_PROJECT_ID;
  } else if (isStaging) {
    functionsProjectId = 'wisetree-ai-planner-staging';
  } else {
    functionsProjectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'wisetree-ai-planner';
  }

  // Local emulator when configured.
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_EMULATORS === 'true') {
    return `http://localhost:${emulatorPorts.functions}/${functionsProjectId}/${region}/${name}`;
  }

  // Deployed: call the Cloud Function directly. (The same-origin /__fn/ Hosting-rewrite approach was reverted —
  // the simple-string rewrite does not resolve to a GEN-2 function, so it 404'd to the SPA and broke every API
  // call in prod. Hiding the region via a rewrite must be re-attempted with the gen-2 explicit form + a staging
  // smoke test before it can ship.)
  return `https://${region}-${functionsProjectId}.cloudfunctions.net/${name}`;
};

const getApiGatewayUrl = (): string => getFunctionUrl('apiGateway');

export interface ApiCallOptions {
  requireAuth?: boolean;
  /** Send the caller's token when someone is signed in, and go anonymous otherwise (public routes that still credit a signed-in viewer). */
  optionalAuth?: boolean;
  timeout?: number;
}

/**
 * Turn a raw transport/HTTP failure into a calm, human message. We NEVER show users a technical
 * string ("Request timeout after 60000ms", "Failed to fetch", a stack, an HTTP status) — those are
 * for the logs. A backend-provided message (our handlers write diplomatic copy) is kept as-is.
 */
export function friendlyApiError(error: any, status?: number): string {
  if (error?.name === 'AbortError') {
    return 'That’s taking longer than usual — give it another try in a moment.';
  }
  const msg = typeof error === 'string' ? error : (error?.message || '');
  if (error instanceof TypeError || /failed to fetch|networkerror|load failed|err_/i.test(msg)) {
    return 'I can’t reach the server right now — check your connection and try again.';
  }
  if (status && status >= 500) return 'Something went wrong on our end — please try again shortly.';
  if (status === 429) return 'A lot’s happening at once — give it a few seconds and try again.';
  // Keep a backend message only if it reads like human copy (not a raw/technical token).
  if (msg && msg.length < 200 && !/\b(undefined|null|TypeError|stack|HTTP \d|status \d|ECONN|fetch)\b/i.test(msg)) return msg;
  return 'Hmm, that didn’t go through — please try again.';
}

/**
 * Call a Cloud Function through the API Gateway
 *
 * @param functionName - Name of the function to call (e.g., 'getUserProjects')
 * @param data - Data to send to the function
 * @param options - Additional options
 * @returns Promise with the function response
 */
export async function callFunction<T = any>(
  functionName: string,
  data?: any,
  options: ApiCallOptions = {}
): Promise<T> {
  const { optionalAuth = false, requireAuth = !optionalAuth, timeout = 60000 } = options;
  /**
   * THE TRAIL WATCHES FROM HERE (TRAIL-b, owner 2026-09-13: log every action and its outcome, so a problem
   * can be replayed instead of guessed).
   *
   * This one seam sees every request the product makes and how each ended, so instrumenting it covers most of
   * the surface at once — far better than sprinkling calls through every component, which would drift the
   * moment somebody adds a button. What it cannot see is a click that never reaches the server; those are
   * logged where they happen.
   */
  const started = Date.now();

  try {
    // Get auth token if required
    let token: string | undefined;
    if (requireAuth) {
      const { getAuth } = await import('firebase/auth');
      const currentUser = getAuth().currentUser;
      if (!currentUser) {
        throw new Error('User not authenticated');
      }
      token = await currentUser.getIdToken();
    } else if (optionalAuth) {
      const { getAuth } = await import('firebase/auth');
      token = await getAuth().currentUser?.getIdToken();
    }

    // Build request URL (calculate dynamically each time to get current hostname)
    const url = `${getApiGatewayUrl()}/${functionName}`;

    // Setup request headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      // Make request
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: data ? JSON.stringify(data) : undefined,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Detect a newer deployed release (the gateway stamps X-App-Version) so a long-open tab can offer a refresh.
      checkAppVersionDrift(response.headers.get('X-App-Version'));

      // Parse response — tolerate a non-JSON infra error page (e.g. a 502/503 HTML body).
      let result: any = {};
      try { result = await response.json(); } catch { /* non-JSON body */ }

      // HTTP error → prefer the backend's (diplomatic) message, else a calm status-based one.
      if (!response.ok) {
        throw new Error(result.error || friendlyApiError(null, response.status));
      }

      // Application-level error envelope.
      if (result.success === false) {
        throw new Error(result.error || friendlyApiError(null, response.status));
      }

      // Return the data
      trail(functionName, 'ok', Date.now() - started);
      return result.data ?? result;
    } catch (error: any) {
      clearTimeout(timeoutId);
      // Timeout (aborted) and network failures (offline / DNS / CORS) get a calm message; an Error
      // already carrying our diplomatic copy is rethrown unchanged.
      if (error?.name === 'AbortError') throw new Error(friendlyApiError({ name: 'AbortError' }));
      if (error instanceof TypeError) throw new Error(friendlyApiError(error));
      throw error;
    }
  } catch (error: any) {
    console.error(`[API Client] Error calling ${functionName}:`, error);
    // The MESSAGE, not the payload: outcomes are what a trail is for, and a request body can carry what
    // somebody typed, which is their content rather than our telemetry.
    trail(functionName, 'error', Date.now() - started, String(error?.message || '').slice(0, 120));
    throw error;
  }
}

/**
 * Record one call's outcome, without ever recursing.
 *
 * THE EXCLUSION IS LOAD-BEARING: the trail ships its own batches through `callFunction`, so logging those
 * would log the logging, forever. The other two are the same family — error and telemetry ingest — and a
 * diagnostic that reports on its own delivery adds noise rather than signal.
 *
 * Imported lazily so the api client keeps no static dependency on a service that imports it back.
 */
const TRAIL_EXEMPT = new Set(['logUiEvents', 'logClientError', 'logTelemetry']);
function trail(functionName: string, outcome: 'ok' | 'error', ms: number, detail?: string): void {
  if (TRAIL_EXEMPT.has(functionName)) return;
  void import('../services/uiTrailService')
    .then((m) => m.logUiEvent(`call:${functionName}`, { outcome, ms, detail }))
    .catch(() => { /* the trail is never allowed to affect the call it is describing */ });
}

